import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import portalT from '../translations/portal';
import axios from 'axios';
import { TrendingUp, Activity, Calendar, Star } from 'lucide-react';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const Analytics = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = portalT[lang];
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loader"></div>
        <p>{lang === 'nl' ? 'Analyses laden...' : 'Loading analytics...'}</p>
      </div>
    );
  }

  const L = lang === 'nl' ? {
    back: 'Terug naar Portaal',
    title: 'Gebruiksanalyses',
    totalSessions: 'TOTAAL SESSIES',
    toolsUsed: 'TOOLS GEBRUIKT',
    accountAge: 'ACCOUNT LEEFTIJD',
    days: 'dagen',
    usageOverTime: 'Gebruik in de Tijd',
    toolDistribution: 'Tool Verdeling',
    noUsage: 'Nog geen gebruiksdata — die verschijnt hier zodra je tools gebruikt.',
    noToolUsage: 'Nog geen tools gebruikt — kies er één om te beginnen.',
    detailedStats: 'Gedetailleerde Tool Statistieken',
    tool: 'Tool',
    usageCount: 'Aantal Keer Gebruikt',
    percentage: 'Percentage',
    toolUsageLabel: 'Toolgebruik',
  } : {
    back: 'Back to Portal',
    title: 'Usage Analytics',
    totalSessions: 'TOTAL SESSIONS',
    toolsUsed: 'TOOLS USED',
    accountAge: 'ACCOUNT AGE',
    days: 'days',
    usageOverTime: 'Usage Over Time',
    toolDistribution: 'Tool Distribution',
    noUsage: 'No usage data yet — it will appear here as you use the tools.',
    noToolUsage: 'No tools used yet — pick one to get started.',
    detailedStats: 'Detailed Tool Statistics',
    tool: 'Tool',
    usageCount: 'Usage Count',
    percentage: 'Percentage',
    toolUsageLabel: 'Tool Usage',
  };

  const toolName = (id, name) => name || t.tools[id]?.title || id.charAt(0).toUpperCase() + id.slice(1);

  const timelineData = {
    labels: analytics?.timeline?.map(d => d.date) || [],
    datasets: [
      {
        label: L.toolUsageLabel,
        data: analytics?.timeline?.map(d => d.count) || [],
        borderColor: '#232323',
        backgroundColor: 'rgba(35, 35, 35, 0.1)',
        tension: 0.4
      }
    ]
  };

  const toolUsageData = {
    labels: analytics?.tool_usage?.map(tl => toolName(tl.tool, tl.tool_name)) || [],
    datasets: [
      {
        data: analytics?.tool_usage?.map(tl => tl.usage_count) || [],
        backgroundColor: ['#16202C', '#E4EDF8', '#FEEFDC', '#b8d1ba'],
        borderColor: ['#987D9C', '#768597', '#BCA182', '#2d6a3d'],
        borderWidth: 2
      }
    ]
  };

  return (
    <div className="tool-page">
      <div className="tool-header">
        <h1 className="heading-1">{L.title}</h1>
      </div>

      <div className="analytics-container">
        {/* Overview Cards */}
        <div className="analytics-grid">
          <div className="analytics-card">
            <div className="analytics-card-icon" style={{ background: '#16202C' }}>
              <Activity size={24} />
            </div>
            <div>
              <p className="caption">{L.totalSessions}</p>
              <h3 className="heading-hero">{analytics?.overview?.total_sessions || 0}</h3>
            </div>
          </div>

          <div className="analytics-card">
            <div className="analytics-card-icon" style={{ background: '#16202C' }}>
              <TrendingUp size={24} />
            </div>
            <div>
              <p className="caption">{L.toolsUsed}</p>
              <h3 className="heading-hero">{analytics?.overview?.tools_used || 0}</h3>
            </div>
          </div>

          <div className="analytics-card">
            <div className="analytics-card-icon" style={{ background: '#16202C' }}>
              <Calendar size={24} />
            </div>
            <div>
              <p className="caption">{L.accountAge}</p>
              <h3 className="heading-hero">{analytics?.overview?.account_age_days || 0} {L.days}</h3>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="charts-grid">
          <div className="chart-card">
            <h3 className="heading-3">{L.usageOverTime}</h3>
            {analytics?.timeline?.length > 0 ? (
              <Line data={timelineData} options={{ responsive: true, maintainAspectRatio: true }} />
            ) : (
              <p className="body-medium text-secondary">{L.noUsage}</p>
            )}
          </div>

          <div className="chart-card">
            <h3 className="heading-3">{L.toolDistribution}</h3>
            {analytics?.tool_usage?.length > 0 ? (
              <Doughnut data={toolUsageData} options={{ responsive: true, maintainAspectRatio: true }} />
            ) : (
              <p className="body-medium text-secondary">{L.noToolUsage}</p>
            )}
          </div>
        </div>

        {/* Tool Stats Table */}
        {analytics?.tool_usage?.length > 0 && (
          <div className="stats-table-card">
            <h3 className="heading-3">{L.detailedStats}</h3>
            <table className="stats-table">
              <thead>
                <tr>
                  <th>{L.tool}</th>
                  <th>{L.usageCount}</th>
                  <th>{L.percentage}</th>
                </tr>
              </thead>
              <tbody>
                {(Array.isArray(analytics.tool_usage) ? analytics.tool_usage : []).map((tool, index) => (
                  <tr key={index}>
                    <td style={{ fontWeight: 500 }}>
                      {toolName(tool.tool, tool.tool_name)}
                    </td>
                    <td>{tool.usage_count}</td>
                    <td>{tool.percentage}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
